import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faEye,
  faEyeSlash,
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import { Input } from "../../../components/ui/input";
import Modal from "../../../components/ui/modal";

const ChangePasswordModal = ({ customer, isOpen, onClose }) => {
  const [form, setForm] = useState({ newPassword: "", confirmPassword: "" });
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: "",
    color: "",
    requirements: {
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      special: false,
    },
  });

  useEffect(() => {
    if (isOpen) {
      setForm({ newPassword: "", confirmPassword: "" });
      setError("");
      setPasswordStrength({
        score: 0,
        label: "",
        color: "",
        requirements: {
          length: false,
          uppercase: false,
          lowercase: false,
          number: false,
          special: false,
        },
      });
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");

    if (name === "newPassword") {
      validatePasswordStrength(value);
    }
  };

  const validatePasswordStrength = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    const metCount = Object.values(requirements).filter(Boolean).length;

    let score = 0;
    let label = "";
    let color = "";

    if (password.length === 0) {
      score = 0;
      label = "";
      color = "";
    } else if (metCount <= 2) {
      score = 1;
      label = "Weak";
      color = "text-red-500";
    } else if (metCount === 3) {
      score = 2;
      label = "Medium";
      color = "text-yellow-500";
    } else if (metCount >= 4) {
      score = 3;
      label = "Strong";
      color = "text-green-500";
    }

    setPasswordStrength({
      score,
      label,
      color,
      requirements,
    });
  };

  const handleSave = () => {
    const { newPassword, confirmPassword } = form;

    if (!newPassword || !confirmPassword) {
      setError("Please fill in all fields.");
      return;
    }

    // Check all requirements
    const requirements = {
      length: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    };

    const allMet = Object.values(requirements).every(Boolean);

    if (!allMet) {
      setError("Password must meet all requirements below.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    // Password is valid
    onClose();
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const getStrengthBarColor = () => {
    if (passwordStrength.score === 0) return "bg-gray-200";
    if (passwordStrength.score === 1) return "bg-red-500";
    if (passwordStrength.score === 2) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getStrengthBarWidth = () => {
    if (passwordStrength.score === 0) return "0%";
    if (passwordStrength.score === 1) return "33%";
    if (passwordStrength.score === 2) return "66%";
    return "100%";
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Change Password"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="gap-2 bg-blue-800 hover:bg-blue-900 text-white w-full sm:w-auto px-6"
          >
            <FontAwesomeIcon icon={faLock} />
            Change Password
          </Button>
        </div>
      }
    >
      {customer && (
        <div className="flex flex-col gap-4 text-sm">
          {/* Customer info */}
          <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faLock}
                className="text-blue-800 h-4 w-4"
              />
            </div>
            <div>
              <p className="text-xs text-gray-600">Changing password for</p>
              <p className="font-medium text-gray-900">{customer.full_name}</p>
              <p className="text-xs text-gray-600">{customer.customer_no}</p>
            </div>
          </div>

          {/* New Password */}
          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium">New Password</label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                name="newPassword"
                value={form.newPassword}
                onChange={handleChange}
                placeholder="Enter new password"
                className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30 pr-10"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>
          </div>

          {/* Password Strength Indicator */}
          {form.newPassword.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-300 ${getStrengthBarColor()}`}
                      style={{ width: getStrengthBarWidth() }}
                    ></div>
                  </div>
                  <span
                    className={`text-xs font-medium ${passwordStrength.color}`}
                  >
                    {passwordStrength.label}
                  </span>
                </div>
              </div>

              {/* Password Requirements */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-1">
                <div
                  className={`flex items-center gap-1.5 text-xs ${passwordStrength.requirements.length ? "text-green-600" : "text-gray-600"}`}
                >
                  {passwordStrength.requirements.length ? (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="text-green-500 h-3 w-3"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      className="text-gray-300 h-3 w-3"
                    />
                  )}
                  <span>At least 8 characters</span>
                </div>
                <div
                  className={`flex items-center gap-1.5 text-xs ${passwordStrength.requirements.uppercase ? "text-green-600" : "text-gray-600"}`}
                >
                  {passwordStrength.requirements.uppercase ? (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="text-green-500 h-3 w-3"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      className="text-gray-300 h-3 w-3"
                    />
                  )}
                  <span>Uppercase letter</span>
                </div>
                <div
                  className={`flex items-center gap-1.5 text-xs ${passwordStrength.requirements.lowercase ? "text-green-600" : "text-gray-600"}`}
                >
                  {passwordStrength.requirements.lowercase ? (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="text-green-500 h-3 w-3"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      className="text-gray-300 h-3 w-3"
                    />
                  )}
                  <span>Lowercase letter</span>
                </div>
                <div
                  className={`flex items-center gap-1.5 text-xs ${passwordStrength.requirements.number ? "text-green-600" : "text-gray-600"}`}
                >
                  {passwordStrength.requirements.number ? (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="text-green-500 h-3 w-3"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      className="text-gray-300 h-3 w-3"
                    />
                  )}
                  <span>Number (0-9)</span>
                </div>
                <div
                  className={`flex items-center gap-1.5 text-xs col-span-1 sm:col-span-2 ${passwordStrength.requirements.special ? "text-green-600" : "text-gray-600"}`}
                >
                  {passwordStrength.requirements.special ? (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="text-green-500 h-3 w-3"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      className="text-gray-300 h-3 w-3"
                    />
                  )}
                  <span>Special character (!@#$%^&*(),.?":{}|&lt;&gt;)</span>
                </div>
              </div>
            </div>
          )}

          {/* Confirm Password */}
          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium">
              Confirm Password
            </label>
            <div className="relative">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30 pr-10"
              />
              <button
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon
                  icon={showConfirmPassword ? faEyeSlash : faEye}
                />
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="w-1 h-6 bg-red-500 rounded-full shrink-0 mt-0.5"></div>
              <p className="text-red-600 text-xs">{error}</p>
            </div>
          )}

          {/* Password Match Indicator */}
          {form.confirmPassword.length > 0 && form.newPassword.length > 0 && (
            <div
              className={`flex items-center gap-2 p-2 rounded-lg ${form.newPassword === form.confirmPassword ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}
            >
              {form.newPassword === form.confirmPassword ? (
                <>
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="text-green-500 h-4 w-4"
                  />
                  <span className="text-green-700 text-xs font-medium">
                    Passwords match
                  </span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon
                    icon={faExclamationCircle}
                    className="text-red-500 h-4 w-4"
                  />
                  <span className="text-red-700 text-xs font-medium">
                    Passwords do not match
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ChangePasswordModal;
